import { useCallback, useEffect, useMemo, MutableRefObject } from 'react';
import { Observable, Subject, EMPTY } from 'rxjs';
import { timeout, catchError, tap, takeUntil, filter } from 'rxjs/operators';
import usePricingSourceStore from 'src/stores/pricing/usePricingSourceStore';
import {
	PricingWebsocketClient,
	ProcessedPricingData,
} from '../PricingWebsocketClient';
import {
	PRICING_WS_TIMEOUT_MS,
	PRICING_FALLBACK_COOLDOWN_MS,
} from '../constants';

interface UsePricingDataPipelineParams {
	lastFallbackTimestamp: MutableRefObject<number>;
	subscriptionRestartKey: number;
}

interface UsePricingDataPipelineReturn {
	createDataPipeline: (
		client: PricingWebsocketClient
	) => Observable<ProcessedPricingData[]>;
	destroyCurrentPipeline$: Subject<void>;
}

/**
 * Hook that creates the RxJS data pipeline for transforming websocket data.
 * Manages the destroyCurrentPipeline$ Subject for cleanup signaling.
 *
 * Similar to useOrderbookDataPipeline, this hook:
 * - Applies timeout to detect stale connections
 * - Auto-switches back to websocket when data flows
 * - Handles errors and triggers fallback to polling
 */
export function usePricingDataPipeline({
	lastFallbackTimestamp,
	subscriptionRestartKey,
}: UsePricingDataPipelineParams): UsePricingDataPipelineReturn {
	const setSource = usePricingSourceStore((s) => s.setSource);

	/**
	 * Subject to signal pipeline destruction.
	 * Recreated when subscriptionRestartKey changes (i.e., on recovery or tab return).
	 */
	const destroyCurrentPipeline$ = useMemo(
		() => new Subject<void>(),
		[subscriptionRestartKey]
	);

	// Cleanup destroyCurrentPipeline$ on unmount or restart
	useEffect(() => {
		return () => {
			destroyCurrentPipeline$.next();
			destroyCurrentPipeline$.complete();
		};
	}, [destroyCurrentPipeline$]);

	const createDataPipeline = useCallback(
		(client: PricingWebsocketClient): Observable<ProcessedPricingData[]> => {
			if (!client) return EMPTY;

			return client.getPricingDataStream().pipe(
				// Dev tool: block messages when flag is set
				filter(() => {
					if (
						typeof window !== 'undefined' &&
						(window as any).DRIFT_DEV_BLOCK_PRICING_WEBSOCKET_MESSAGES
					) {
						return false;
					}
					return true;
				}),

				// Auto-switch back to websocket when data flows (if in polling mode)
				tap(() => {
					const currentSource = usePricingSourceStore.getState().source;
					if (currentSource === 'polling') {
						const timeSinceLastFallback =
							Date.now() - lastFallbackTimestamp.current;
						const cooldownComplete =
							timeSinceLastFallback > PRICING_FALLBACK_COOLDOWN_MS;

						if (cooldownComplete) {
							setSource('websocket');
						}
					}
				}),

				// Timeout: if no data within PRICING_WS_TIMEOUT_MS, trigger fallback
				timeout({
					first: PRICING_WS_TIMEOUT_MS,
					each: PRICING_WS_TIMEOUT_MS,
				}),

				// Handle errors (including timeout)
				catchError((error) => {
					if (error.name === 'TimeoutError') {
						lastFallbackTimestamp.current = Date.now();
						setSource('polling');
					}
					// Return EMPTY to complete the stream, recovery will handle reconnection
					return EMPTY;
				}),

				takeUntil(destroyCurrentPipeline$)
			);
		},
		[setSource, destroyCurrentPipeline$, lastFallbackTimestamp]
	);

	return {
		createDataPipeline,
		destroyCurrentPipeline$,
	};
}
